/**
 * Insurance Connector Content
 *
 * State-driven component rendering for the insurance connector.
 * For form-flow steps, the LoadingBar and navigation buttons are
 * rendered OUTSIDE the AnimatePresence so they stay stable during
 * step transitions — only the form content animates.
 */

"use client";

import { AnimatePresence, motion } from "framer-motion";
import { BirthdayInput } from "./components/BirthdayInput";
import { DisambiguationQuestion } from "./components/DisambiguationQuestion";
import { EligibilityAgeRejection } from "./components/EligibilityAgeRejection";
import { EligibilityBirthdayInput } from "./components/EligibilityBirthdayInput";
import { EligibilityMinorConsentReferral } from "./components/EligibilityMinorConsentReferral";
import { EligibilityStateQuestion } from "./components/EligibilityStateQuestion";
import { EligibilityStateRejection } from "./components/EligibilityStateRejection";
import { EligibilityWelcome } from "./components/EligibilityWelcome";
import { InsuranceError } from "./components/InsuranceError";
import { InsurancePayer } from "./components/InsurancePayer";
import { InsuredStateInput } from "./components/InsuredStateInput";
import { IntroScreen } from "./components/IntroScreen";
import { LoadingBar } from "./components/LoadingBar";
import { MemberIdInput } from "./components/MemberIdInput";
import { WorkplaceInjuryQuestion } from "./components/WorkplaceInjuryQuestion";
import { NameInput } from "./components/NameInput";
import { OTPInput } from "./components/OTPInput";
import { PhoneNumberInput } from "./components/PhoneNumberInput";
import { SecondaryQuestion } from "./components/SecondaryQuestion";
import { StepNavigation } from "./components/StepNavigation";
import { UnverifiedResult } from "./components/UnverifiedResult";
import { LoadingScreen } from "./components/LoadingScreen";
import { useInsuranceConnectorState } from "./insurance-connector-provider";
import { getStepConfig } from "./step-config";

const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

export function InsuranceConnectorContent() {
  const state = useInsuranceConnectorState();

  const renderComponent = () => {
    if (state.matches({ full: "eligibilityWelcome" })) {
      return <EligibilityWelcome />;
    }
    if (state.matches({ full: "eligibilityBirthdayInput" })) {
      return <EligibilityBirthdayInput />;
    }
    if (state.matches({ full: "eligibilityAgeRejection" })) {
      return <EligibilityAgeRejection />;
    }
    if (state.matches({ full: "eligibilityStateQuestion" })) {
      return <EligibilityStateQuestion />;
    }
    if (state.matches({ full: "eligibilityStateRejection" })) {
      return <EligibilityStateRejection />;
    }
    if (state.matches({ full: "eligibilityMinorConsentReferral" })) {
      return <EligibilityMinorConsentReferral />;
    }
    if (state.matches({ full: "introScreen" })) {
      return <IntroScreen />;
    }
    if (state.matches({ full: "workplaceInjuryQuestion" })) {
      return <WorkplaceInjuryQuestion />;
    }
    if (state.matches({ full: "nameInput" })) {
      return <NameInput />;
    }
    if (state.matches({ full: "birthdayInput" })) {
      return <BirthdayInput />;
    }
    if (state.matches({ full: "phoneNumberInput" })) {
      return <PhoneNumberInput />;
    }
    if (state.matches({ full: "checkingReturnUser" })) {
      return <LoadingScreen />;
    }
    if (state.matches({ full: "disambiguationQuestion" })) {
      return <DisambiguationQuestion />;
    }
    if (state.matches({ full: "disambiguating" })) {
      return <LoadingScreen />;
    }
    if (state.matches({ full: "sendingSMS" })) {
      return <LoadingScreen />;
    }
    if (state.matches({ full: "otpInput" })) {
      return <OTPInput />;
    }
    if (state.matches({ full: "verifyingSMS" })) {
      return <LoadingScreen />;
    }
    if (state.matches({ full: "insurancePayer" })) {
      return <InsurancePayer />;
    }
    if (state.matches({ full: "memberIdInput" })) {
      return <MemberIdInput />;
    }
    if (state.matches({ full: "insuredStateInput" })) {
      return <InsuredStateInput />;
    }
    if (state.matches({ full: "secondaryQuestion" })) {
      return <SecondaryQuestion />;
    }
    if (state.matches({ full: "secondaryPayerInput" })) {
      return <InsurancePayer />;
    }
    if (state.matches({ full: "secondaryMemberIdInput" })) {
      return <MemberIdInput />;
    }
    if (state.matches({ full: "secondaryInsuredStateInput" })) {
      return <InsuredStateInput />;
    }
    if (state.matches({ full: "loadingInsurance" })) {
      return <LoadingScreen />;
    }
    if (state.matches({ full: "unverifiedResult" })) {
      return <UnverifiedResult />;
    }
    if (state.matches({ full: "unverifiedRetryPayer" })) {
      return <InsurancePayer />;
    }
    if (state.matches({ full: "unverifiedRetryMemberId" })) {
      return <MemberIdInput />;
    }
    if (state.matches({ full: "unverifiedRetryInsuredState" })) {
      return <InsuredStateInput />;
    }
    if (state.matches({ full: "unverifiedRetryName" })) {
      return <NameInput />;
    }
    if (state.matches({ full: "unverifiedSecondResult" })) {
      return <UnverifiedResult />;
    }
    if (state.matches({ full: "inactiveResult" })) {
      return <InsuranceError />;
    }
    if (state.matches({ full: "outOfNetworkResult" })) {
      return <InsuranceError />;
    }

    if (state.matches("minimized")) {
      return null;
    }
    if (state.matches("closed")) {
      return null;
    }
    if (state.matches("done")) {
      return null;
    }

    return null;
  };

  const component = renderComponent();

  if (!component) {
    return null;
  }

  const stateKey = state.matches("full")
    ? `full-${(state.value as { full: string }).full}`
    : String(state.value);

  const fullState = state.matches("full")
    ? (state.value as { full: string }).full
    : "";
  const stepConfig = getStepConfig(fullState);

  if (stepConfig) {
    return (
      <div className="p-0 pt-2 md:pt-8 h-full flex flex-col">
        <div className="flex-1 min-h-0 flex flex-col gap-5 md:gap-12">
          {stepConfig.showLoadingBar && <LoadingBar />}
          <div className="flex-1 min-h-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={stateKey}
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="h-full"
              >
                {component}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <StepNavigation config={stepConfig} />
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={stateKey}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="w-full h-full"
      >
        {component}
      </motion.div>
    </AnimatePresence>
  );
}
